import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import BarcodeReader from 'react-barcode-reader';
import { getOr, omit, isNumber, isEmpty, get } from 'lodash/fp';
import {
  Grid,
  Row,
  Col,
  Icon,
  Input,
  DateRangePicker,
  Button,
  CheckPicker,
  SelectPicker,
  ButtonToolbar
} from 'rsuite';
import styled from 'styled-components';
import * as Status from '../../../constants/reservation/Status';
import StatusLabels from '../../../constants/reservation/StatusLabels';
import {
  getTrackingMethodsOptions,
  getActiveTrackingMethodsOptions
} from '../../../redux/Inventory/selectors/components/search-options';
import { fetchTrackingMethodOptions } from '../../../redux/Inventory/sagas/routines';
import { Status as TrackingMethodStatus } from '../../../constants/tracking-method';

const StyledSearchInput = styled(Input)`
  &::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    font-size: 11px;
  }
  &::-moz-placeholder {
    /* Firefox 19+ */
    font-size: 11px;
  }
  &:-ms-input-placeholder {
    /* IE 10+ */
    font-size: 11px;
  }
  &:-moz-placeholder {
    /* Firefox 18- */
    font-size: 11px;
  }
`;

const HiddenIcon = styled(Icon)`
  float: right;
  margin-right: 20px;
`;

const Search = ({
  searchQuery,
  queryOptions,
  placeholder,
  withRequestTime,
  withStatus,
  onChange,
  onSubmit,
  onClear,
  onInit,
  isFetching,
  exportButton,
  printButton,
  statusOptions,
  showHiddenHelpDesired
}) => {
  const dispatch = useDispatch();
  const trackingMethodOptions = useSelector(getTrackingMethodsOptions);
  const activeTrackingMethodOptions = useSelector(
    getActiveTrackingMethodsOptions
  );
  const helpDesiredOptions = showHiddenHelpDesired
    ? trackingMethodOptions
    : activeTrackingMethodOptions;

  useEffect(() => {
    onInit();
    if (isEmpty(trackingMethodOptions)) {
      dispatch(fetchTrackingMethodOptions());
    }
    return function cleanup() {
      const includeCookie = false;
      onClear(includeCookie);
    };
  }, []);

  const handleOnChangeBarcorde = code => {
    if (code) onChange({ ...searchQuery, code });
    else onChange(omit('code', searchQuery));
  };

  const handleOnChangeQuery = q => {
    if (q) onChange({ ...searchQuery, q });
    else onChange(omit('q', searchQuery));
  };
  const handleOnChangeRequestTime = requestTime => {
    onChange({ ...searchQuery, requestTime });
  };

  const handleOnChangeStatus = status => {
    if (isNumber(status)) onChange({ ...searchQuery, status });
    else onChange(omit('status', searchQuery));
  };

  const handleOnChangeTrackingMethods = trackingMethodId => {
    if (!isEmpty(trackingMethodId))
      onChange({ ...searchQuery, trackingMethodId });
    else onChange(omit('trackingMethodId', searchQuery));
  };

  const handleScan = code => {
    onChange({ ...searchQuery, code });
    onSubmit({ ...queryOptions, code });
  };

  return (
    <Grid fluid style={{ padding: 0, marginLeft: 0 }}>
      <Row gutter={8}>
        <Col sm={4} lg={2}>
          <BarcodeReader minLength={4} onScan={handleScan} />
          <StyledSearchInput
            type="number"
            max="9999"
            pattern="\d{4}"
            inputMode="numeric"
            value={getOr('', 'code', queryOptions)}
            onChange={handleOnChangeBarcorde}
            style={{ paddingLeft: 10 }}
            placeholder="Search Barcode"
            onKeyPress={({ key }) => {
              if (key === 'Enter') onSubmit(queryOptions);
            }}
          />
        </Col>
        <Col sm={6} lg={4}>
          <StyledSearchInput
            value={getOr('', 'q', queryOptions)}
            onChange={handleOnChangeQuery}
            style={{ paddingLeft: 10 }}
            placeholder={placeholder}
            onKeyPress={({ key }) => {
              if (key === 'Enter') onSubmit(queryOptions);
            }}
          />
        </Col>
        <Col sm={6} lg={3}>
          <CheckPicker
            style={{ width: '100%' }}
            placeholder="Help Desired"
            value={getOr('', 'trackingMethodId', queryOptions)}
            data={helpDesiredOptions}
            onChange={handleOnChangeTrackingMethods}
            renderMenuItem={(label, item) => (
              <span>
                {label}
                {get('meta.status', item) === TrackingMethodStatus.INACTIVE && (
                  <HiddenIcon icon="eye-slash" />
                )}
              </span>
            )}
          />
        </Col>
        {withRequestTime && (
          <Col sm={6} lg={3}>
            <DateRangePicker
              style={{ width: '100%' }}
              value={getOr([], 'requestTime', queryOptions)}
              placeholder="Select date range"
              onChange={handleOnChangeRequestTime}
            />
          </Col>
        )}
        {withStatus && (
          <Col sm={4} lg={2}>
            <SelectPicker
              style={{ width: 130, marginRight: 10 }}
              value={getOr('', 'status', queryOptions)}
              data={statusOptions}
              appearance="default"
              placeholder="Status"
              onChange={handleOnChangeStatus}
            />
          </Col>
        )}
        <Col sm={14} lg={7} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <ButtonToolbar>
            <Button
              color="green"
              disabled={isFetching}
              onClick={() => onSubmit(queryOptions)}
            >
              <Icon icon="search" /> Search
            </Button>
            <Button disabled={isFetching} onClick={onClear}>
              <Icon icon="refresh" /> Reset
            </Button>
            {!isFetching && exportButton}
            {!isFetching && printButton}
          </ButtonToolbar>
        </Col>
      </Row>
    </Grid>
  );
};

const SearchQueryType = PropTypes.shape({
  q: PropTypes.string,
  createdAt: PropTypes.arrayOf(PropTypes.shape({})),
  tagId: PropTypes.arrayOf(PropTypes.number)
});

const StatusOption = PropTypes.shape({
  value: PropTypes.number,
  label: PropTypes.string
});

Search.propTypes = {
  placeholder: PropTypes.string,
  searchQuery: SearchQueryType,
  queryOptions: SearchQueryType,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onInit: PropTypes.func,
  isFetching: PropTypes.bool.isRequired,
  withRequestTime: PropTypes.bool,
  withStatus: PropTypes.bool,
  exportButton: PropTypes.node,
  printButton: PropTypes.node,
  statusOptions: PropTypes.arrayOf(StatusOption),
  showHiddenHelpDesired: PropTypes.bool
};

Search.defaultProps = {
  placeholder: 'Search ID, Guest ID/Name or Outreach',
  searchQuery: {},
  queryOptions: {},
  onInit: () => {},
  withRequestTime: false,
  withStatus: false,
  exportButton: null,
  printButton: null,
  statusOptions: [
    { value: Status.PENDING, label: StatusLabels[Status.PENDING] },
    { value: Status.ACTIVE, label: StatusLabels[Status.ACTIVE] },
    { value: Status.CONFIRMED, label: StatusLabels[Status.CONFIRMED] },
    { value: Status.REJECTED, label: StatusLabels[Status.REJECTED] },
    { value: Status.CANCELED, label: StatusLabels[Status.CANCELED] },
    { value: Status.COMPLETED, label: StatusLabels[Status.COMPLETED] }
  ],
  showHiddenHelpDesired: false
};

export default Search;
